import {
  Component,
  EventEmitter,
  Inject,
  Input,
  LOCALE_ID, OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import {Dossiers, RecruteService, StateEnum, TransitionEnum, Transitions} from '../../common/services';

import {MatDialog} from '@angular/material/dialog';
import {PopulationEnum} from '../../common/services';
import {rolesDar} from '../../common/services/roles/roles-dar';
import {IndividuService, PubSubService} from '@Common/core/services';
import {ValidationButtonComponent} from './validation-button/validation-button.component';
import {TransitionAction} from './transition-action.interface';

import {RecruteValidationData} from './recrute-validation.interface';
import {DarDossiersService} from '../../common/services/dossiers/dossiers.service';
import {concatMap, finalize} from 'rxjs/operators';
import {RecruteFormModel} from '../common/recrute/recrute-form.model';
import {Observable, of} from 'rxjs';
import {SubSink} from '@Common/core/utils/subsink';
import {DatePipe} from '@angular/common';
import {RecruteValidationDataModel} from './validation-button/recrute-validation-data.model';
import {RemunerationEnum} from '../../common/services/enums/remunerationEnum';
import {DataTransitionValidationModel} from './data-transition-validation.model';

/**
 * Gère les boutons à afficher selon l'étape et le rôle
 * A partir de l'état d'un dossier, calcul les boutons à afficher selon les transitions qui partent de l'état courant du dossier
 */
@Component({
  selector: 'app-button-transitions',
  templateUrl: './button-transition.component.html',
  styleUrls: ['./button-transition.component.scss']
})
export class ButtonTransitionComponent implements OnInit, OnChanges, OnDestroy {
  @Input() state: StateEnum = StateEnum.none;
  @Input() dossier: Dossiers = null;
  @Input() transitions: Transitions[] = [];
  @Input() disabled = false;
  @Input() disabledReason = '';

  // TG-317 indiciaire => INM recrut depend du parametrage de la motivation, horaire => INM non obligatoire
  @Input() hasINMRecrutement = false;

  /**
   * prise en compte roles <==> boutons à afficher ou non, cf. buttonsState
   * si pour un état du dossier, le connecté à tel rôle alors aucun bouton ne sera affiché (on n'affiche pas les boutons transitions)
   */
  @Input() buttonsByRoles = false;

  /**
   * Les transitions qui demandent un avis (MS, SBTCP, DRH)
   */
  @Input() avisTransitions: TransitionEnum[] = [
    TransitionEnum.validation_sbtcp,
    TransitionEnum.validation_drh,
    TransitionEnum.validation_commission];

  /**
   * La transition à exécuter
   */
  @Output() goTransition: EventEmitter<any> = new EventEmitter<any>();

  /**
   * Pour le calcul de la liste des transitions selon l'état du dossier, détermine les boutons à cliquer en bas
   * Contient également selon l'étape la saisie du recruté et/ou le gestionnaire SPTA (pour notif) ou de la date de prise de fonction SPTA / SPTE
   */
  listTransitions: TransitionAction[] = [];

  /**
   * Liste des rôles pour tel état qui ne sont pas concernés par l'affichage des boutons d'actions transitions
   * lorsque l'état a des transitions, valable si buttonsByRole = true
   */
  buttonsState: Map<StateEnum, rolesDar[]> = new Map<StateEnum, rolesDar[]>([
    [StateEnum.refus, [rolesDar.commission_consult]],
    [StateEnum.commission, [rolesDar.commission_consult]],
    [StateEnum.commission_decision, [rolesDar.commission_consult]]
  ]);

  rolesConnecte: rolesDar[] = [];
  subSink = new SubSink();

  loading = true;

  constructor(@Inject(LOCALE_ID) private locale: string,
              private dialog: MatDialog,
              private pubSubSvc: PubSubService,
              private individuSvc: IndividuService,
              private recruteSvc: RecruteService,
              private dossierSvc: DarDossiersService) {

  }

  ngOnInit() {
    this.rolesConnecte = [];
    this.subSink.sink = this.individuSvc.individu$.subscribe(_individu => {
      if (this.individuSvc.hasRole(_individu, rolesDar.commission_consult)) {
        this.rolesConnecte.push(rolesDar.commission_consult);
      }
      this.makeTransitions();
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes?.hasINMRecrutement?.firstChange === false) {
      this.makeTransitions();
    }
  }
  /**
   * Message de confirmation selon le bouton (transition)
   * @param transition
   */
  confirmationEtapeLabel(transition: TransitionEnum) {
    let message = '';
    /*const _transitionMessage: Map<StateEnum, string> = new Map([
      [TransitionEnum.validation_masse_salariale,
      `Confirmez-vous l'envoi de votre demande à la <strong>Dipefas</strong> de votre dossier (<strong>vous ne pourrez plus le modifier</strong>) ?`],
    ]);*/
    const _msgPrefix = 'Confirmez-vous ';
    switch (transition) {
      case TransitionEnum.validation_masse_salariale:
        // tslint:disable-next-line:max-line-length
        message = `Confirmez-vous l'envoi de votre demande à la <strong>Dipefas</strong> de votre dossier (<strong>vous ne pourrez plus le modifier</strong>) ?`;
        break;
      case TransitionEnum.validation_sbtcp:
        message = `Confirmez-vous l'envoi au <strong>SBTCP</strong> du dossier ?`;
        break;
      case TransitionEnum.validation_drh:
        message = `Confirmez-vous l'envoi à l'étape <strong>DRH</strong> du dossier ?`;
        break;
      case TransitionEnum.validation_spta:
        message = `Confirmez-vous l'envoi au <strong>SPTA</strong> du dossier ?`;
        break;
      case TransitionEnum.validation_commission:
        message = `Confirmez-vous l'envoi à la <strong>commission</strong> du dossier ?`;
        break;
      case TransitionEnum.validation_acceptee_commission:
        message = `Confirmez-vous la <strong>validation</strong> du dossier ?`;
        break;
      case TransitionEnum.validation_partielle_commission:
        message = `Confirmez-vous la <strong>validation partielle</strong> du dossier ?`;
        break;
      case TransitionEnum.demande_refusee:
        message = `Confirmez-vous le <strong>REFUS</strong> du dossier ?`;
        break;
      case TransitionEnum.annulation_decision_commission:
        message = `Confirmez-vous le retour en <strong>commission</strong> du dossier ?`;
        break;
      case TransitionEnum.recrutement_interne_sans_publication:
        message = `Confirmez-vous le <strong>recrutement sans publication</strong> du dossier ?`;
        break;
      case TransitionEnum.recrutement_externe:
        message = `Confirmez-vous le <strong>recrutement externe</strong> du dossier ?`;
        break;
      case TransitionEnum.recrutement_interne:
        message = `Confirmez-vous le <strong>recrutement interne</strong> du dossier ?`;
        break;
      case TransitionEnum.recrutement_interne_externe:
        message = `Confirmez-vous le <strong>recrutement interne externe</strong> du dossier ?`;
        break;
      case TransitionEnum.recrutement_publication:
        message = `Confirmez-vous la <strong>publication</strong> du dossier ?`;
        break;
      case TransitionEnum.publication_spta_traitement:
        message = `Confirmez-vous l'envoi du <strong>traitement</strong> par le SPTA du dossier ?`;
        break;
      case TransitionEnum.spta_traitement_clos:
        message = `Confirmez-vous la <strong>finalisation</strong> du traitement du dossier ?`;
        break;
      case TransitionEnum.recrutement_spte:
        message = `Confirmez-vous le <strong>recrutement SPTE</strong> du dossier ?`;
        break;
      case TransitionEnum.spte_traitement_clos:
        message = `Confirmez-vous la <strong>finalisation SPTE</strong> du traitement du dossier ?`;
        break;

    }

    return message;
  }

  getMessageTransition(transition) {
    let message = '';

    switch (transition) {
      case TransitionEnum.validation_masse_salariale:
        message = 'traitement Dipefas';
        break;
      default:
        message = transition;
        break;
    }

    return message;
  }

  /**
   * Emet la transition
   * @param transition
   * @private
   */
  private _goTransition(transition) {
    const findTransition = this.avisTransitions.find((t) => t === transition);
    if (findTransition) {
      // TODO : vérifier que l'avis est mis avant d'enclencher la transition ?
    }
    const message = this.getMessageTransition(transition);

    this.goTransition.emit({transition: transition, message: message});
  }

  private _setDatePriseFonction(data: RecruteValidationDataModel) {
    const dt: DatePipe = new DatePipe(this.locale);
    this.dossier.date_prise_fonction = data?.datePriseFonction ? dt.transform(data.datePriseFonction, 'yyyy-MM-dd') : null;
  }
  private _setINMrecrutement(data: RecruteValidationDataModel) {
    this.dossier.inm_recrutement = data?.recrute?.INMRecrutement;
  }
  /**
   * Patch du dossier pour gestionnaire et recruté le cas échéant (suppression)
   * Patch de la date de prise de fonction
   * @param data
   * @param {RecruteFormModel} recrute
   * @param transition
   * @private
   */
  private _updateDossierGestionnaire(data: RecruteValidationDataModel, recrute: RecruteFormModel, transition) {
    this.dossier.gestionnaire = recrute?.gestionnaire;
    this._setDatePriseFonction(data);
    this._setINMrecrutement(data);
    this.pubSubSvc.publish('loading', true);
    this.subSink.sink = this.dossierSvc
      .patch(this.dossier, 'uuid')
      .pipe(finalize(() => this.pubSubSvc.publish('loading', false)),
            concatMap(_ => {
              if (recrute?.recrute?.id) { // fix TG-309 TG-308
                this.dossier.recrute = null;
                return this.recruteSvc.delete(recrute.recrute);
              } else {
                return of(null);
              }
      }))
      .subscribe((_) => this._goTransition(transition));
  }
  private _patchDossierRecrute(recrute_id, recrute) {
    this.dossier.recrute = recrute_id;
    this.dossier.gestionnaire = recrute.gestionnaire;
    return this.dossierSvc.patch(this.dossier, 'uuid');
  }
  private _updateDossierRecrute(recrute): Observable<any> {
    return this.recruteSvc.updateOrcreate(recrute.recrute)
      .pipe(concatMap(_recrut => this._patchDossierRecrute(_recrut.id, recrute)));
  }
  private _manageRecruteOrDateFonction(transition, data: RecruteValidationDataModel) {
    const recrute = data.recrute as RecruteFormModel;
    this._setINMrecrutement(data);
    if (data.hasRecrute && recrute?.recrute?.first_name) { // ajout du gestionnaire et recruté, le dossier avance
        this.pubSubSvc.publish('loading', true);
        this._updateDossierRecrute(recrute)
          .pipe(finalize(() => this.pubSubSvc.publish('loading', false)),
                concatMap(_ => {
                  if (data.hasDate && data.datePriseFonction) {
                    this._setDatePriseFonction(data);
                    return this.dossierSvc.patch(this.dossier, 'uuid');
                  } else {
                    return of(null);
                  }
          })).subscribe(_ => this._goTransition(transition));
    } else { // mise à jour gestionnaire le cas échéant et suppression recrute le cas échéant
      this._updateDossierGestionnaire(data, recrute, transition);
    }
  }
  /**
   * confirmation validation du bouton étape prochaine
   * - A partir de la commission :
   *   En modale : affichage des Observations commission
   * - A partir de la publication :
   *   En modale : possibilité de saisir le nom / prénom du recruté voire la sélection du spta gest pour certaines étapes
   * - A partir de validation SPTA ou de la Publication :
   *   Dans la même modale : saisie de l'INM recrutement
   * pour envoi à la dernière étape de traitement SPTA
   * - Possibilité de saisir la date de prise de fonction à la finalisation du dossier (SPTA / SPTE)
   * @param {TransitionAction} transitionAction : la transition à exécuter / vers l'état aller, au clic du bouton étape suivante
   */
  public click(transitionAction: TransitionAction) {
    const transition = transitionAction.transition;
    console.log(this.dossier.observations_commission);
    const _recrute: RecruteValidationData = <RecruteValidationData>{
      title: 'Confirmation d\'envoi',
      message: this.confirmationEtapeLabel(transition),
      recrute: this.dossier.recrute_obj,
      gestionnaire: this.dossier.gestionnaire,
      ...transitionAction
    };
    const _data: DataTransitionValidationModel = {
      recrute: _recrute,
      dossier: this.dossier,
      transition: transitionAction.transition
    };
    const dialogRef = this.dialog.open(ValidationButtonComponent, {data: _data});
    dialogRef.updatePosition({top: '40px'});
    dialogRef.disableClose = true;
    dialogRef.afterClosed().subscribe((result: RecruteValidationDataModel) => {
      if (result) {
        this._manageRecruteOrDateFonction(transition, result);
      }
    });
  }

  private mustHaveButtons() {
    let haveButtons = true;

    if (this.buttonsByRoles && this.buttonsState.has(this.state)) {
      const roles = this.buttonsState.get(this.state);
      roles.forEach((_role) => {
        const yes = this.rolesConnecte.some((_r) => _r === _role);
        if (yes) {
          haveButtons = false;
        }
      });
    }

    return haveButtons;
  }

  /**
   * Création des transitions possibles et donc des boutons sur l'interface selon l'état du dossier
   * dépend également de buttonsByRoles : si true et que le connecté est dans un rôle concerné de l'état buttonsState alors pas de boutons
   */
  private makeTransitions() {
    this.listTransitions = [];
    const haveButtons = this.mustHaveButtons();
    if ( ! haveButtons) {
      return;
    }

    switch (this.state) {
      case StateEnum.demande:
        this.listTransitions.push({transition: TransitionEnum.validation_masse_salariale, hasRecrute: false, hasSptaGestionnaire: false});
        break;
      case StateEnum.masse_salariale:
        this.listTransitions.push({transition: TransitionEnum.validation_sbtcp, hasRecrute: false, hasSptaGestionnaire: false});
        break;
      case StateEnum.sbtcp:
        this.listTransitions.push({transition: TransitionEnum.validation_drh, hasRecrute: false, hasSptaGestionnaire: false});
        break;
      case StateEnum.drh:
        if (this.dossier.type_demande === PopulationEnum.biatoss) {
          this.listTransitions.push(
            {transition: TransitionEnum.validation_commission, hasRecrute: false, hasSptaGestionnaire: false},
            {transition: TransitionEnum.validation_spta, hasRecrute: true, hasSptaGestionnaire: false});
        }

        if (this.dossier.type_demande === PopulationEnum.enseignant) { // pour les enseignants (SPTE), passage obligé en commission
          this.listTransitions.push({transition: TransitionEnum.validation_commission, hasRecrute: false, hasSptaGestionnaire: false});
        }
        break;
      case StateEnum.spta:
        this.listTransitions.push({transition: TransitionEnum.recrutement_interne_sans_publication, hasRecrute: true, hasSptaGestionnaire: true, hasINMrecrutement: this.hasINMRecrutement});
        this.listTransitions.push({transition: TransitionEnum.recrutement_interne_externe, hasRecrute: false, hasSptaGestionnaire: false});
        this.listTransitions.push({transition: TransitionEnum.recrutement_externe, hasRecrute: false, hasSptaGestionnaire: false});
        break;
      case StateEnum.commission:
        this.listTransitions.push({transition: TransitionEnum.validation_acceptee_commission, hasRecrute: false, hasSptaGestionnaire: false});
        this.listTransitions.push({transition: TransitionEnum.validation_partielle_commission, hasRecrute: false, hasSptaGestionnaire: false});
        this.listTransitions.push({transition: TransitionEnum.demande_refusee, hasRecrute: false, hasSptaGestionnaire: false});
        break;
      case StateEnum.commission_decision:
        this.listTransitions.push({transition: TransitionEnum.annulation_decision_commission, hasRecrute: false, hasSptaGestionnaire: false});
        break;
      case StateEnum.recrutement:
        this.listTransitions.push({transition: TransitionEnum.recrutement_publication, hasRecrute: false, hasSptaGestionnaire: false});
        break;
      case StateEnum.publication:
        this.listTransitions.push({transition: TransitionEnum.publication_spta_traitement, hasRecrute: true, hasSptaGestionnaire: true, hasINMrecrutement: this.hasINMRecrutement});
        break;
      case StateEnum.spta_traitement:
        this.listTransitions.push({transition: TransitionEnum.spta_traitement_clos, hasRecrute: false, hasSptaGestionnaire: false, hasDatePriseFonction: true});
        break;
      case StateEnum.refus:
        this.listTransitions.push({transition: TransitionEnum.retour_commission, hasRecrute: false, hasSptaGestionnaire: false});
        break;
      case StateEnum.spte:
        this.listTransitions.push({transition: TransitionEnum.recrutement_spte, hasRecrute: false, hasSptaGestionnaire: true});
        break;
      case StateEnum.recrutement_spte:
        this.listTransitions.push({transition: TransitionEnum.spte_traitement_clos, hasRecrute: false, hasSptaGestionnaire: false, hasDatePriseFonction: true});
        break;
    }
  }

  /**
   * Selon la transition, création du libellé du bouton
   * @param transition
   */
  public getLabelButton(transition: TransitionEnum) {
    /*const _transtionLabelButton: Map<TransitionEnum, string> = new Map<TransitionEnum, string>([
      [TransitionEnum.validation_masse_salariale, 'Enregistrer et envoyer ma demande'],
      [TransitionEnum.validation_sbtcp, 'Envoyer SBTCP'],
      [TransitionEnum.validation_drh, 'Envoyer DRH'],
      [TransitionEnum.validation_spta, 'Envoyer SPTA'],
      [TransitionEnum.validation_commission, 'Envoyer Commission'],
      [TransitionEnum.annulation_decision_commission, 'Annulation décision commission'],
      [TransitionEnum.validation_decision_commission_spta, 'Validation décision, envoyer SPTA'],
      [TransitionEnum.validation_decision_commission_spte, 'Validation décision, envoyer SPTE'],
    ]);*/
    switch (transition) {
      case TransitionEnum.validation_masse_salariale:
        return 'Enregistrer et envoyer ma demande';
      case TransitionEnum.validation_sbtcp:
        return 'Envoyer SBTCP';
      case TransitionEnum.validation_drh:
        return 'Envoyer DRH';
      case TransitionEnum.validation_spta:
        return 'Envoyer SPTA';
      case TransitionEnum.validation_commission:
        return 'Envoyer Commission';
      case TransitionEnum.annulation_decision_commission:
        return 'Annulation décision commission';
      case TransitionEnum.validation_decision_commission_spta:
        return 'Validation décision, envoyer SPTA';
      case TransitionEnum.validation_decision_commission_spte:
        return 'Validation décision, envoyer SPTE';
      case TransitionEnum.validation_acceptee_commission:
        return 'Acceptée';
      case TransitionEnum.validation_partielle_commission:
        return 'Acceptée partiellement';
      case TransitionEnum.demande_refusee:
        return 'Demande refusée';
      case TransitionEnum.retour_commission:
        return 'Remettre en commission';
      case TransitionEnum.recrutement_interne_sans_publication:
        return 'Envoyer recrutement sans publication';
      case TransitionEnum.recrutement_externe:
        return 'Envoyer recrutement externe';
      case TransitionEnum.recrutement_interne:
        return 'Envoyer recrutement interne';
      case TransitionEnum.recrutement_interne_externe:
        return 'Envoyer recrutement interne externe';
      case TransitionEnum.recrutement_publication:
        return 'Envoyer publication';
      case TransitionEnum.publication_spta_traitement:
        return 'Envoyer personne recrutée pour traitement SPTA';
      case TransitionEnum.spta_traitement_clos:
        return 'Envoyer traitement dossier finalisé';
      case TransitionEnum.recrutement_spte:
        return 'Envoyer recrutement SPTE';
      case TransitionEnum.spte_traitement_clos:
        return 'Envoyer traitement dossier finalisé';
    }
  }

  /**
   * Trouve une transition dans la liste des transitions d'état en cours
   * @param transition
   * @private
   */
  private _findTransition(transition: TransitionEnum) {
    let find;

    if (this.transitions && this.transitions.length > 0) {
      find = this.transitions.find(t => t.name === transition);
      return find;
    }

    return null;
  }

  /**
   * Couleur du bouton à afficher selon la transition
   * @param transition
   */
  public getColorButton(transition: TransitionEnum) {
    const trans = this._findTransition(transition);

    if (trans) {
      return `dar-${trans.target}`;
    }

    return '';
  }

  ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }
}
